import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TOOTH_CONDITION_LABELS,
  TOOTH_MOBILITY_GRADES,
  TOOTH_MOBILITY_LABELS,
  readToothMobility,
  TOOTH_CROWN_LABELS,
  TOOTH_FLAG_LABELS,
  TOOTH_SURFACES,
  TOOTH_SURFACE_LABELS,
} from '@helios/data-access';
import type { ToothState, ToothCondition } from './OdontogramChart';
import { ChipListField, type Chip } from './ChipListField';

// ─── Constants ───
//
// PRD.316 — codes and labels come from the canonical vocabulary in data-access
// so Clinical Findings and the chart cannot drift apart. Only the swatch colors
// stay here: those are presentation, not clinical identity.

const GENERAL_FLAGS = (['ok', 'missing', 'root_fragment', 'implant', 'impacted'] as const).map(
  (code) => ({ code, label: TOOTH_FLAG_LABELS[code] }),
);

const CONDITION_COLORS: Record<string, string> = {
  caries: '#dc2626',
  composite: '#3b82f6',
  amalgam: '#6b7280',
  recurrent_caries: '#f97316',
  // Cervical lesions: a warm non-carious family, kept clear of the reds used
  // for active decay so they are not mistaken for caries at a glance.
  cervical_abrasion: '#0d9488',
  cervical_attrition: '#7c3aed',
  cervical_abfraction: '#db2777',
};

const CONDITION_TYPES = (
  [
    'caries',
    'composite',
    'amalgam',
    'recurrent_caries',
    'cervical_abrasion',
    'cervical_attrition',
    'cervical_abfraction',
  ] as const
).map((code) => ({ code, label: TOOTH_CONDITION_LABELS[code], color: CONDITION_COLORS[code] }));

const CROWN_OPTIONS = [
  { code: '', label: 'None' },
  ...(['ceramic', 'pfm', 'gold', 'metal'] as const).map((code) => ({
    code,
    label: TOOTH_CROWN_LABELS[code],
  })),
];

const SURFACES = TOOTH_SURFACES;
const SURFACE_LABELS = TOOTH_SURFACES.map((s) => TOOTH_SURFACE_LABELS[s]);

// ─── Helpers ───

function defaultToothState(): ToothState {
  return { flags: ['ok'], conditions: [], crown: null };
}

// ─── Props ───

/**
 * One diagnosis already recorded against the open tooth.
 *
 * Sourced from the canonical `patient_diagnosis_entries` /
 * `patient_diagnosis_teeth` domain, never from chart JSON.
 */
export interface ToothDiagnosis {
  id: string;
  label: string;
}

interface ToothConditionsModalProps {
  open: boolean;
  toothNum: number;
  initialState: ToothState | undefined;
  onSave: (toothNum: number, state: ToothState) => void;
  onClear: (toothNum: number) => void;
  onClose: () => void;
  primary: string;

  // ─── Diagnosis (canonical domain; optional so non-encounter chart previews
  // can mount this modal without it) ───

  /** Diagnoses already linked to THIS tooth. */
  diagnoses?: ToothDiagnosis[];
  /** Suggestion list for the picker — org history merged with the catalogue. */
  diagnosisSuggestions?: string[];
  /** Notifies the container so it can re-query suggestions as the user types. */
  onDiagnosisSearchChange?: (text: string) => void;
  onAddDiagnosis?: (label: string) => void;
  onRemoveDiagnosis?: (diagnosisId: string) => void;
  /** Read-only chart (e.g. viewing another encounter) — hides the picker. */
  diagnosisReadOnly?: boolean;
}

// ─── Component ───

export function ToothConditionsModal({
  open,
  toothNum,
  initialState,
  onSave,
  onClear,
  onClose,
  primary,
  diagnoses,
  diagnosisSuggestions = [],
  onDiagnosisSearchChange,
  onAddDiagnosis,
  onRemoveDiagnosis,
  diagnosisReadOnly = false,
}: ToothConditionsModalProps) {
  const [flags, setFlags] = useState<string[]>(['ok']);

  // Derived straight from props: the modal holds NO local diagnosis state, so
  // what it shows is always what is stored, and reopening cannot show stale
  // selections.
  const diagnosisChips: Chip[] = useMemo(
    () => (diagnoses ?? []).map((d) => ({ id: d.id, label: d.label })),
    [diagnoses],
  );
  const [conditions, setConditions] = useState<ToothCondition[]>([]);
  const [crown, setCrown] = useState<string | null>(null);
  // `null` = not recorded. Distinct from the explicit 'none' grade, which means
  // the tooth WAS assessed and found firm.
  const [mobility, setMobility] = useState<string | null>(null);

  // Reset when tooth changes
  useEffect(() => {
    if (!open) return;
    const s = initialState ?? defaultToothState();
    setFlags([...s.flags]);
    setConditions(s.conditions.map((c) => ({ ...c, surfaces: [...c.surfaces] })));
    setCrown(s.crown);
    setMobility(readToothMobility(s));
  }, [open, toothNum, initialState]);

  const toggleFlag = useCallback((code: string) => {
    setFlags((prev) => {
      if (code === 'ok') return ['ok'];
      const without = prev.filter((f) => f !== 'ok');
      if (without.includes(code)) {
        const result = without.filter((f) => f !== code);
        return result.length === 0 ? ['ok'] : result;
      }
      return [...without, code];
    });
  }, []);

  const toggleConditionSurface = useCallback((condCode: string, surface: string) => {
    setConditions((prev) => {
      const existing = prev.find((c) => c.code === condCode);
      if (existing) {
        const newSurfaces = existing.surfaces.includes(surface)
          ? existing.surfaces.filter((s) => s !== surface)
          : [...existing.surfaces, surface];
        if (newSurfaces.length === 0) {
          return prev.filter((c) => c.code !== condCode);
        }
        return prev.map((c) => (c.code === condCode ? { ...c, surfaces: newSurfaces } : c));
      }
      return [...prev, { code: condCode, surfaces: [surface] }];
    });
  }, []);

  const handleSave = useCallback(() => {
    // Only write `mobility` when something was actually recorded, so an
    // untouched tooth never gains a fabricated assessment.
    onSave(toothNum, { flags, conditions, crown, ...(mobility ? { mobility } : {}) });
    onClose();
  }, [toothNum, flags, conditions, crown, mobility, onSave, onClose]);

  const handleClear = useCallback(() => {
    onClear(toothNum);
    onClose();
  }, [toothNum, onClear, onClose]);

  if (!open) return null;

  const checkboxEl = (checked: boolean, onClick: () => void) => (
    <div
      onClick={onClick}
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        border: checked ? `2px solid ${primary}` : '1.5px solid #d1d5db',
        background: checked ? primary : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          // Widened so the whole-tooth flags and the Diagnosis picker fit on
          // one row. `maxWidth` keeps it inside small viewports, where the top
          // row wraps instead.
          width: 780,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '85vh',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-5)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: primary,
                textAlign: 'center',
              }}
            >
              Part Conditions
            </h3>
          </div>
          <div
            style={{
              height: 28,
              padding: '0 10px',
              borderRadius: 14,
              background: primary,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Part #{toothNum}
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* Top row: whole-tooth flags on the left, Diagnosis on the right.
              `flex-wrap` (not absolute positioning) means the Diagnosis block
              drops onto its own line when the modal is narrow, so it can never
              overlap the surface matrix below. */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--sp-4)',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'center',
                minHeight: 32,
                // Shrinks to make room for the Diagnosis column beside it;
                // `minWidth: 0` is what allows a flex item to shrink below its
                // content width instead of forcing a wrap.
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {GENERAL_FLAGS.map((f) => (
                <label
                  key={f.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    fontWeight: flags.includes(f.code) ? 600 : 400,
                  }}
                  onClick={() => toggleFlag(f.code)}
                >
                  {checkboxEl(flags.includes(f.code), () => {})}
                  {f.label}
                </label>
              ))}
            </div>

            {/* Diagnosis — writes straight to the canonical diagnosis domain,
                scoped to THIS tooth. Multi-value, because a tooth can carry
                several diagnoses in that domain; chips rather than a
                multi-select keep a long list manageable.

                Rendered only when the container supplies handlers: chart
                previews mount this modal without an encounter to write to. */}
            {onAddDiagnosis && onRemoveDiagnosis && (
              // Shrinks to its content and is pushed flush against the modal's
              // right edge by `margin-left: auto`, rather than occupying a
              // fixed column whose contents would sit left inside it.
              // `flex-wrap` on the parent still drops the whole block onto its
              // own row on a narrow viewport, so it never overlaps the surface
              // matrix below.
              <div
                style={{
                  flex: '0 1 auto',
                  marginLeft: 'auto',
                  maxWidth: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                {/* No "Diagnosis" heading in the editable case — the
                    "+ Add diagnosis" pill already names the field. The
                    read-only view keeps one, because bare pills there have
                    nothing else identifying them. */}
                {diagnosisReadOnly ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Diagnosis
                    </span>
                    {diagnosisChips.length === 0 ? (
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                        None recorded
                      </span>
                    ) : (
                      diagnosisChips.map((c) => (
                        <span
                          key={c.id}
                          style={{
                            padding: '2px 10px',
                            borderRadius: 999,
                            background: 'var(--bg-surface-2, #f1f5f9)',
                            border: '1px solid var(--border)',
                            fontSize: 'var(--font-size-xs)',
                          }}
                        >
                          {c.label}
                        </span>
                      ))
                    )}
                  </div>
                ) : (
                  <ChipListField
                    chips={diagnosisChips}
                    suggestions={diagnosisSuggestions}
                    onSearchChange={onDiagnosisSearchChange}
                    onAdd={onAddDiagnosis}
                    onRemove={(chip) => onRemoveDiagnosis(chip.id)}
                    addLabel="Add diagnosis"
                    placeholder="Search diagnosis…"
                    // The catalogue is only discoverable if it is shown; making
                    // the user guess a first letter hides 40 curated options.
                    browseOnOpen
                    // The field sits against the modal's right edge, so the
                    // popover opens inward.
                    alignSuggestions="right"
                  />
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--border)' }} />

          {/* Conditions table — each row has label + 5 surface checkboxes */}
          <div>
            {/* Surface header row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '140px repeat(5, 1fr)',
                gap: 4,
                marginBottom: 8,
                paddingLeft: 0,
              }}
            >
              <div />
              {SURFACE_LABELS.map((s) => (
                <span
                  key={s}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    fontWeight: 'var(--font-weight-medium)',
                    textAlign: 'center',
                  }}
                >
                  {s}
                </span>
              ))}
            </div>

            {/* Condition rows */}
            {CONDITION_TYPES.map((cond) => {
              const existing = conditions.find((c) => c.code === cond.code);
              return (
                <div
                  key={cond.code}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '140px repeat(5, 1fr)',
                    gap: 4,
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: existing ? 600 : 400,
                      color: existing ? cond.color : 'var(--text)',
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: cond.color,
                        opacity: 0.8,
                        flexShrink: 0,
                      }}
                    />
                    {cond.label}
                  </div>
                  {SURFACES.map((surf) => (
                    <div key={surf} style={{ display: 'flex', justifyContent: 'center' }}>
                      {checkboxEl(existing?.surfaces.includes(surf) ?? false, () =>
                        toggleConditionSurface(cond.code, surf),
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Crown select */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                width: 60,
              }}
            >
              Crown
            </span>
            <select
              value={crown ?? ''}
              onChange={(e) => setCrown(e.target.value || null)}
              style={{
                height: 36,
                padding: '0 12px',
                fontSize: 'var(--font-size-sm)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                minWidth: 160,
              }}
            >
              {CROWN_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Mobility — a structured periodontal grade, not a coloured
              condition, so it sits on its own row and coexists with everything
              above. "Not recorded" is the default and stays that way unless the
              clinician chooses a grade; "None" is an explicit assessment. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              marginTop: 'var(--sp-3)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                width: 60,
              }}
            >
              Mobility
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[{ code: '', label: 'Not recorded' }]
                .concat(
                  TOOTH_MOBILITY_GRADES.map((g) => ({ code: g, label: TOOTH_MOBILITY_LABELS[g] })),
                )
                .map((opt) => {
                  const active = (mobility ?? '') === opt.code;
                  return (
                    <button
                      key={opt.code || 'unset'}
                      type="button"
                      onClick={() => setMobility(opt.code || null)}
                      style={{
                        height: 30,
                        padding: '0 12px',
                        borderRadius: 'var(--radius-full)',
                        border: `1px solid ${active ? primary : 'var(--border)'}`,
                        background: active ? primary : 'transparent',
                        color: active ? '#fff' : 'var(--text-muted)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--border)',
            gap: 'var(--sp-3)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              height: 40,
              padding: '0 24px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleClear}
            style={{
              height: 40,
              padding: '0 24px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Clear Chart
          </button>
          <button
            onClick={handleSave}
            style={{
              height: 40,
              padding: '0 32px',
              borderRadius: 'var(--radius-lg)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
            }}
          >
            Save Chart
          </button>
        </div>
      </div>
    </div>
  );
}
